import { useEffect, useState, useMemo } from "react";
import {
  Button,
  Table,
  Spin,
  Alert,
  Modal,
  Space,
  message,
} from "antd";
import { ReloadOutlined, FileExcelOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";
import { getBdpoReportBdpo, getBdpoGpReport } from "../api/User";
/* =====================================================
   COMPONENT : BDPO REPORT (BLOCK LEVEL)
===================================================== */
const GramSabhaBDPOReport = ({ dateFilter }) => {
  /* ================= STATE ================= */
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [gpCurrentPage, setGpCurrentPage] = useState(1);
  const [gpPageSize, setGpPageSize] = useState(10);

  /* -------- GP Modal -------- */
  const [gpModalOpen, setGpModalOpen] = useState(false);
  const [gpModalTitle, setGpModalTitle] = useState("");
  const [gpData, setGpData] = useState([]);
  const [gpLoading, setGpLoading] = useState(false);


  const blockTotals = useMemo(() => {
    if (!data.length) return {
      total_gp: 0, gs_scheduled: 0, gs_conducted: 0,
      voter_updated: 0, attendance_updated: 0,
      image_uploaded: 0, proceeding_book_uploaded: 0,
      meeting_video_uploaded: 0, income_verified: 0,
      scheme_verified: 0, development_work_uploaded: 0,
      amrit_sarovar_uploaded: 0,
    };

    return data.reduce((acc, row) => ({
      total_gp: acc.total_gp + row.total_gp,
      gs_scheduled: acc.gs_scheduled + row.gs_scheduled,
      gs_conducted: acc.gs_conducted + row.gs_conducted,
      voter_updated: acc.voter_updated + row.voter_updated,
      attendance_updated: acc.attendance_updated + row.attendance_updated,
      image_uploaded: acc.image_uploaded + row.image_uploaded,
      proceeding_book_uploaded: acc.proceeding_book_uploaded + row.proceeding_book_uploaded,
      meeting_video_uploaded: acc.meeting_video_uploaded + row.meeting_video_uploaded,
      income_verified: acc.income_verified + row.income_verified,
      scheme_verified: acc.scheme_verified + row.scheme_verified,
      development_work_uploaded: acc.development_work_uploaded + row.development_work_uploaded,
      amrit_sarovar_uploaded: acc.amrit_sarovar_uploaded + row.amrit_sarovar_uploaded,
    }), {
      total_gp: 0, gs_scheduled: 0, gs_conducted: 0,
      voter_updated: 0, attendance_updated: 0,
      image_uploaded: 0, proceeding_book_uploaded: 0,
      meeting_video_uploaded: 0, income_verified: 0,
      scheme_verified: 0, development_work_uploaded: 0,
      amrit_sarovar_uploaded: 0,
    });
  }, [data]);




  const gpTotals = useMemo(() => {
    if (!gpData.length) return {
      gs_scheduled: 0, gs_conducted: 0, voter_updated: 0,
      attendance_updated: 0, image_uploaded: 0,
      proceeding_book_uploaded: 0, meeting_video_uploaded: 0,
      income_verified: 0, scheme_verified: 0,
      development_work_uploaded: 0, amrit_sarovar_uploaded: 0,
    };

    return gpData.reduce((acc, row) => ({
      gs_scheduled: acc.gs_scheduled + row.gs_scheduled,
      gs_conducted: acc.gs_conducted + row.gs_conducted,
      voter_updated: acc.voter_updated + row.voter_updated,
      attendance_updated: acc.attendance_updated + row.attendance_updated,
      image_uploaded: acc.image_uploaded + row.image_uploaded,
      proceeding_book_uploaded: acc.proceeding_book_uploaded + row.proceeding_book_uploaded,
      meeting_video_uploaded: acc.meeting_video_uploaded + row.meeting_video_uploaded,
      income_verified: acc.income_verified + row.income_verified,
      scheme_verified: acc.scheme_verified + row.scheme_verified,
      development_work_uploaded: acc.development_work_uploaded + row.development_work_uploaded,
      amrit_sarovar_uploaded: acc.amrit_sarovar_uploaded + row.amrit_sarovar_uploaded,
    }), {
      gs_scheduled: 0, gs_conducted: 0, voter_updated: 0,
      attendance_updated: 0, image_uploaded: 0,
      proceeding_book_uploaded: 0, meeting_video_uploaded: 0,
      income_verified: 0, scheme_verified: 0,
      development_work_uploaded: 0, amrit_sarovar_uploaded: 0,
    });
  }, [gpData]);




  // ================= SORT HELPERS =================
  // ================= SORT HELPERS =================
  const textSorter = (a, b, key) => {
    const valA = (a[key] || "").toString().toLowerCase();
    const valB = (b[key] || "").toString().toLowerCase();
    return valA.localeCompare(valB);
  };

  const numberSorter = (a, b, key) => {
    const numA = parseFloat(a[key]) || 0;
    const numB = parseFloat(b[key]) || 0;
    return numA - numB;
  };

  /* ================= MAIN TABLE COLUMNS ================= */
  const columns = [
    {
      title: "Block Name",
      dataIndex: "block_name",
      width: 180,
      align: "center",
      sorter: (a, b) => textSorter(a, b, "block_name"), // Use textSorter for text
      sortDirections: ["ascend", "descend"],

    },
    {
      title: "Total Gram Panchayat",
      dataIndex: "total_gp",
      width: 180,
      align: "center",
      sorter: (a, b) => numberSorter(a, b, "total_gp"), // Use numberSorter for numbers
      sortDirections: ["ascend", "descend"],

      render: (value, record) => (
        <span onClick={() => openGPFromBlock(record)} style={clickStyle}>
          {value}
        </span>
      ),
    },
    {
      title: "Total Gram Panchayat where Gram Sabha Scheduled",
      dataIndex: "gs_scheduled",
      width: 320,
      align: "center",
      sorter: (a, b) => numberSorter(a, b, "gs_scheduled"),
      sortDirections: ["ascend", "descend"],

    },
    {
      title: "Total Gram Panchayat where Gram Sabha Conducted",
      dataIndex: "gs_conducted",
      width: 320,
      align: "center",
      sorter: (a, b) => numberSorter(a, b, "gs_conducted"),
      sortDirections: ["ascend", "descend"],

    },
    {
      title: "No. of Panchayat where Voter Number Updated",
      dataIndex: "voter_updated",
      width: 320,
      align: "center",
      sorter: (a, b) => numberSorter(a, b, "voter_updated"),
      sortDirections: ["ascend", "descend"],

    },
    {
      title: "No. of Panchayat where Attendance Updated",
      dataIndex: "attendance_updated",
      width: 320,
      align: "center",
      sorter: (a, b) => numberSorter(a, b, "attendance_updated"),
      sortDirections: ["ascend", "descend"],

    },
    {
      title: "Number of Panchayat where Image Uploaded",
      dataIndex: "image_uploaded",
      width: 320,
      align: "center",
      sorter: (a, b) => numberSorter(a, b, "image_uploaded"),
      sortDirections: ["ascend", "descend"],

    },
    {
      title: "Number of Panchayat where Proceeding Book Uploaded",
      dataIndex: "proceeding_book_uploaded",
      width: 360,
      align: "center",
      sorter: (a, b) => numberSorter(a, b, "proceeding_book_uploaded"),
      sortDirections: ["ascend", "descend"],

    },
    {
      title: "Number of Panchayat where Meeting Video Uploaded",
      dataIndex: "meeting_video_uploaded",
      width: 360,
      align: "center",
      sorter: (a, b) => numberSorter(a, b, "meeting_video_uploaded"),
      sortDirections: ["ascend", "descend"],

    },
    {
      title: "Income Verified",
      dataIndex: "income_verified",
      width: 180,
      align: "center",
      sorter: (a, b) => numberSorter(a, b, "income_verified"),
      sortDirections: ["ascend", "descend"],

    },
    {
      title: "Scheme Verified",
      dataIndex: "scheme_verified",
      width: 180,
      align: "center",
      sorter: (a, b) => numberSorter(a, b, "scheme_verified"),
      sortDirections: ["ascend", "descend"],

    },
    {
      title: "Number of Panchayat where Development Work Uploaded",
      dataIndex: "development_work_uploaded",
      width: 380,
      align: "center",
      sorter: (a, b) => numberSorter(a, b, "development_work_uploaded"),
      sortDirections: ["ascend", "descend"],

    },
    {
      title: "Total Panchayat where Detail of Amrit Sarovar Uploaded",
      dataIndex: "amrit_sarovar_uploaded",
      width: 380,
      align: "center",
      sorter: (a, b) => numberSorter(a, b, "amrit_sarovar_uploaded"),
      sortDirections: ["ascend", "descend"],

    },
  ];

  /* ================= GP MODAL COLUMNS ================= */
  const gpColumns = [

    {
      title: "Block Name", dataIndex: "block_name", width: 180, align: "center", sorter: (a, b) => textSorter(a, b, "block_name"),
      sortDirections: ["ascend", "descend"],
      fixed: "left"
    },
    {
      title: "Gram Panchayat Name", dataIndex: "gp_name", width: 260, align: "center", sorter: (a, b) => textSorter(a, b, "gp_name"),
      sortDirections: ["ascend", "descend"],
      fixed: "left"
    },
    {
      title: "GS Scheduled", dataIndex: "gs_scheduled", width: 120, sorter: (a, b) => numberSorter(a, b, "gs_scheduled"),
      sortDirections: ["ascend", "descend"],
      align: "center"
    },
    {
      title: "GS Conducted", dataIndex: "gs_conducted", width: 120, sorter: (a, b) => numberSorter(a, b, "gs_conducted"),
      sortDirections: ["ascend", "descend"],
      align: "center"
    },

    {
      title: "Voter Updated", dataIndex: "voter_updated", width: 120, sorter: (a, b) => numberSorter(a, b, "voter_updated"),
      sortDirections: ["ascend", "descend"],
      align: "center"
    },

    {
      title: "Attendance Updated", dataIndex: "attendance_updated", width: 140, sorter: (a, b) => numberSorter(a, b, "attendance_updated"),
      sortDirections: ["ascend", "descend"],
      align: "center"
    },
    {
      title: "Image Uploaded", dataIndex: "image_uploaded", width: 130, sorter: (a, b) => numberSorter(a, b, "image_uploaded"),
      sortDirections: ["ascend", "descend"],
      align: "center"
    },
    {
      title: "Proceeding Book", dataIndex: "proceeding_book_uploaded", width: 150, sorter: (a, b) => numberSorter(a, b, "proceeding_book_uploaded"),
      sortDirections: ["ascend", "descend"],
      align: "center"
    },

    {
      title: "Meeting Video", dataIndex: "meeting_video_uploaded", width: 150, sorter: (a, b) => numberSorter(a, b, "meeting_video_uploaded"),
      sortDirections: ["ascend", "descend"],
      align: "center"
    },
    {
      title: "Income Verified", dataIndex: "income_verified", width: 120, sorter: (a, b) => numberSorter(a, b, "income_verified"),
      sortDirections: ["ascend", "descend"],
      align: "center"
    },
    {
      title: "Scheme Verified", dataIndex: "scheme_verified", width: 120, sorter: (a, b) => numberSorter(a, b, "scheme_verified"),
      sortDirections: ["ascend", "descend"],
      align: "center"
    },
    {
      title: "Development Work", dataIndex: "development_work_uploaded", width: 160, sorter: (a, b) => numberSorter(a, b, "development_work_uploaded"),
      sortDirections: ["ascend", "descend"],
      align: "center"
    },
    {
      title: "Amrit Sarovar", dataIndex: "amrit_sarovar_uploaded", width: 150, sorter: (a, b) => numberSorter(a, b, "amrit_sarovar_uploaded"),
      sortDirections: ["ascend", "descend"],
      align: "center"
    },
  ];

  /* ================= FETCH BLOCK DATA ================= */
  useEffect(() => {
    fetchBlockData();
  }, [dateFilter]);

  const fetchBlockData = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = sessionStorage.getItem("token");

      if (!token) {
        setError("No authentication token found. Please login again.");
        setLoading(false);
        return;
      }

      const result = await getBdpoReportBdpo(token, { period: dateFilter || undefined });

      if (result.status === 'success') {
        setData(result.data.map((item) => ({
          block_code: item.block_code,
          block_name: item.block_name,

          total_gp: Number(item.total_gp),
          gs_scheduled: Number(item.gs_scheduled),
          gs_conducted: Number(item.gs_conducted),
          voter_updated: Number(item.voter_updated),
          attendance_updated: Number(item.attendance_updated),
          image_uploaded: Number(item.image_uploaded),
          proceeding_book_uploaded: Number(item.proceeding_book_uploaded),
          meeting_video_uploaded: Number(item.meeting_video_uploaded),
          income_verified: Number(item.income_verified),
          scheme_verified: Number(item.scheme_verified),
          development_work_uploaded: Number(item.development_work_uploaded),
          amrit_sarovar_uploaded: Number(item.amrit_sarovar_uploaded),
        })));

      } else {
        setError(result.message || 'Failed to fetch block data');
      }
    } catch (err) {
      console.error('Error fetching block data:', err);
      setError('Failed to fetch block data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /* ================= DRILL FUNCTION ================= */
  const openGPFromBlock = async (block) => {
    console.log('Opening GP modal for block:', block.block_name, 'Block Code:', block.block_code);
    setGpModalTitle(`Gram Panchayat-wise Report : ${block.block_name}`);
    setGpModalOpen(true);
    setGpData([]); // Clear previous data
    setGpLoading(true); // Start loading

    try {
      const token = sessionStorage.getItem("token");

      if (!token) {
        console.error('No token found in sessionStorage');
        setError("No authentication token found. Please login again.");
        setGpLoading(false);
        return;
      }

      console.log('Calling getBdpoGpReport API with block_code:', block.block_code);
      const result = await getBdpoGpReport(token, block.block_code);
      console.log('API Response:', result);

      if (result.status === 'success') {
        console.log('GP Data received:', result.data.length, 'records');
        setGpData(result.data.map((item, index) => ({
          key: index + 1,
          district_name: block.district_name,
          block_name: block.block_name,
          gp_name: item.gp_name || item.panchayat_name,
          gs_scheduled: item.gs_scheduled || 0,
          gs_conducted: item.gs_conducted || 0,
          voter_updated: item.voter_updated || 0,
          attendance_updated: item.attendance_updated || 0,
          image_uploaded: item.image_uploaded || 0,
          proceeding_book_uploaded: item.proceeding_book_uploaded || 0,
          meeting_video_uploaded: item.meeting_video_uploaded || 0,
          income_verified: item.income_verified || 0,
          scheme_verified: item.scheme_verified || 0,
          development_work_uploaded: item.development_work_uploaded || 0,
          amrit_sarovar_uploaded: item.amrit_sarovar_uploaded || 0,
        })));
      } else {
        console.error('Failed to fetch GP data:', result.message);
        setError(`Failed to fetch GP data: ${result.message}`);
        setGpData([]);
      }
    } catch (err) {
      console.error('Error fetching GP data:', err);
      setError('Failed to fetch GP data. Please try again.');
      setGpData([]);
    } finally {
      setGpLoading(false); // Stop loading
    }
  };

  /* ================= EXPORT FUNCTIONS ================= */

  // Export main block table to Excel
  const handleExportBlockExcel = () => {
    if (!data || data.length === 0) {
      message.warning("No data to export");
      return;
    }

    try {
      const excelData = data.map((row, idx) => ({
        "Sr. No.": idx + 1,
        "Block Name": row.block_name || "-",
        "Total GP": row.total_gp || 0,
        "GS Scheduled": row.gs_scheduled || 0,
        "GS Conducted": row.gs_conducted || 0,
        "Voter Updated": row.voter_updated || 0,
        "Attendance Updated": row.attendance_updated || 0,
        "Image Uploaded": row.image_uploaded || 0,
        "Proceeding Book Uploaded": row.proceeding_book_uploaded || 0,
        "Meeting Video Uploaded": row.meeting_video_uploaded || 0,
        "Income Verified": row.income_verified || 0,
        "Scheme Verified": row.scheme_verified || 0,
        "Development Work Uploaded": row.development_work_uploaded || 0,
        "Amrit Sarovar Uploaded": row.amrit_sarovar_uploaded || 0,
      }));
      excelData.push({
        "Sr. No.": "",
        "Block Name": "GRAND TOTAL",
        "Total GP": blockTotals.total_gp,
        "GS Scheduled": blockTotals.gs_scheduled,
        "GS Conducted": blockTotals.gs_conducted,
        "Voter Updated": blockTotals.voter_updated,
        "Attendance Updated": blockTotals.attendance_updated,
        "Image Uploaded": blockTotals.image_uploaded,
        "Proceeding Book Uploaded": blockTotals.proceeding_book_uploaded,
        "Meeting Video Uploaded": blockTotals.meeting_video_uploaded,
        "Income Verified": blockTotals.income_verified,
        "Scheme Verified": blockTotals.scheme_verified,
        "Development Work Uploaded": blockTotals.development_work_uploaded,
        "Amrit Sarovar Uploaded": blockTotals.amrit_sarovar_uploaded,
      });


      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Block Report");

      const fileName = `BDPO_Block_Report_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(workbook, fileName);

      message.success("Excel file exported successfully");
    } catch (err) {
      console.error("Export Excel error:", err);
      message.error("Failed to export Excel file");
    }
  };

  // Export GP modal data to Excel
  const handleExportGpExcel = () => {
    if (!gpData || gpData.length === 0) {
      message.warning("No data to export");
      return;
    }

    try {
      const excelData = gpData.map((row, idx) => ({
        "Sr. No.": idx + 1,

        "Block Name": row.block_name || "-",
        "GP Name": row.gp_name || "-",
        "GS Scheduled": row.gs_scheduled || 0,
        "GS Conducted": row.gs_conducted || 0,
        "Voter Updated": row.voter_updated || 0,
        "Attendance Updated": row.attendance_updated || 0,
        "Image Uploaded": row.image_uploaded || 0,
        "Proceeding Book Uploaded": row.proceeding_book_uploaded || 0,
        "Meeting Video Uploaded": row.meeting_video_uploaded || 0,
        "Income Verified": row.income_verified || 0,
        "Scheme Verified": row.scheme_verified || 0,
        "Development Work Uploaded": row.development_work_uploaded || 0,
        "Amrit Sarovar Uploaded": row.amrit_sarovar_uploaded || 0,
      }));

      excelData.push({
        "Sr. No.": "",
        "Block Name": "GRAND TOTAL",
        "GP Name": "",
        "GS Scheduled": gpTotals.gs_scheduled,
        "GS Conducted": gpTotals.gs_conducted,
        "Voter Updated": gpTotals.voter_updated,
        "Attendance Updated": gpTotals.attendance_updated,
        "Image Uploaded": gpTotals.image_uploaded,
        "Proceeding Book Uploaded": gpTotals.proceeding_book_uploaded,
        "Meeting Video Uploaded": gpTotals.meeting_video_uploaded,
        "Income Verified": gpTotals.income_verified,
        "Scheme Verified": gpTotals.scheme_verified,
        "Development Work Uploaded": gpTotals.development_work_uploaded,
        "Amrit Sarovar Uploaded": gpTotals.amrit_sarovar_uploaded,
      });

      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "GP Report");

      const fileName = `BDPO_GP_Report_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(workbook, fileName);

      message.success("Excel file exported successfully");
    } catch (err) {
      console.error("Export Excel error:", err);
      message.error("Failed to export Excel file");
    }
  };

  /* ================= UI ================= */
  return (
    <div className="report-container">
      <div className="report-card">
        <div className="report-header">
          <div>
            <h2 className="report-title">Detailed report of Special Gram Sabha (BDPO)</h2>

          </div>

          <Space>
            <Button
              type="primary"
              icon={<FileExcelOutlined />}
              onClick={handleExportBlockExcel}
              disabled={loading || data.length === 0}
            >
              Export Excel
            </Button>
            <Button icon={<ReloadOutlined />} onClick={fetchBlockData}>
              Refresh
            </Button>
          </Space>
        </div>

        {error && <Alert type="error" message={error} />}

        {loading ? (
          <Spin />
        ) : (
          <Table
            columns={columns}
            dataSource={data}
            rowKey={(record) => record.block_code}
            bordered
            size="small"
            scroll={{ x: 3200, y: 420 }}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: data.length,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50", "100"],
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} records`,
            }}
            onChange={(pagination) => {
              setCurrentPage(pagination.current);
              setPageSize(pagination.pageSize);
            }}
          />
        )}
        <div style={totalBoxStyle}>
          <span><strong>Totals:</strong></span>

          <span>Total GP: <strong>{blockTotals.total_gp.toLocaleString()}</strong></span>
          <span>GS Scheduled: <strong>{blockTotals.gs_scheduled.toLocaleString()}</strong></span>
          <span>GS Conducted: <strong>{blockTotals.gs_conducted.toLocaleString()}</strong></span>
          <span>Voter Updated: <strong>{blockTotals.voter_updated.toLocaleString()}</strong></span>
          <span>Attendance Updated: <strong>{blockTotals.attendance_updated.toLocaleString()}</strong></span>
          <span>Image Uploaded: <strong>{blockTotals.image_uploaded.toLocaleString()}</strong></span>
          <span>Proceeding Book: <strong>{blockTotals.proceeding_book_uploaded.toLocaleString()}</strong></span>
          <span>Meeting Video: <strong>{blockTotals.meeting_video_uploaded.toLocaleString()}</strong></span>
          <span>Income Verified: <strong>{blockTotals.income_verified.toLocaleString()}</strong></span>
          <span>Scheme Verified: <strong>{blockTotals.scheme_verified.toLocaleString()}</strong></span>
          <span>Dev Work: <strong>{blockTotals.development_work_uploaded.toLocaleString()}</strong></span>
          <span>Amrit Sarovar: <strong>{blockTotals.amrit_sarovar_uploaded.toLocaleString()}</strong></span>
        </div>
      </div>




      {/* ========== GP MODAL ========== */}
      <Modal
        open={gpModalOpen}
        onCancel={() => setGpModalOpen(false)}
        width={1200}
        destroyOnClose
        footer={null}
        closable
        style={{ top: 20 }}
        bodyStyle={{
          padding: '16px 0 0 0',
          overflow: 'hidden'
        }}
        title={
          <div style={{ paddingRight: 40 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span style={{ fontSize: 16, fontWeight: 600 }}>
                {gpModalTitle}
              </span>

              <Button
                type="primary"
                icon={<FileExcelOutlined />}
                onClick={handleExportGpExcel}
                disabled={gpLoading || gpData.length === 0}
              >
                Export Excel
              </Button>
            </div>
          </div>
        }
      >


        {gpLoading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin size="large" />
          </div>
        ) : (
          <Table
            columns={gpColumns}
            dataSource={gpData}
            rowKey="key"
            bordered
            size="small"
            scroll={{ x: 1600, y: 400 }}
            pagination={{
              current: gpCurrentPage,
              pageSize: gpPageSize,
              total: gpData.length,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50", "100"],
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} records`,
            }}
            onChange={(pagination) => {
              setGpCurrentPage(pagination.current);
              setGpPageSize(pagination.pageSize);
            }}
          />

        )}
        <div style={totalBoxStyle}>
          <span><strong>Totals:</strong></span>

          <span>GS Scheduled: <strong>{gpTotals.gs_scheduled.toLocaleString()}</strong></span>
          <span>GS Conducted: <strong>{gpTotals.gs_conducted.toLocaleString()}</strong></span>
          <span>Voter Updated: <strong>{gpTotals.voter_updated.toLocaleString()}</strong></span>
          <span>Attendance Updated: <strong>{gpTotals.attendance_updated.toLocaleString()}</strong></span>
          <span>Image Uploaded: <strong>{gpTotals.image_uploaded.toLocaleString()}</strong></span>
          <span>Proceeding Book: <strong>{gpTotals.proceeding_book_uploaded.toLocaleString()}</strong></span>
          <span>Meeting Video: <strong>{gpTotals.meeting_video_uploaded.toLocaleString()}</strong></span>
          <span>Income Verified: <strong>{gpTotals.income_verified.toLocaleString()}</strong></span>
          <span>Scheme Verified: <strong>{gpTotals.scheme_verified.toLocaleString()}</strong></span>
          <span>Dev Work: <strong>{gpTotals.development_work_uploaded.toLocaleString()}</strong></span>
          <span>Amrit Sarovar: <strong>{gpTotals.amrit_sarovar_uploaded.toLocaleString()}</strong></span>
        </div>


      </Modal>
    </div>

  );

};

/* ================= CLICK STYLE ================= */
const clickStyle = {
  color: "#1677ff",
  cursor: "pointer",
  textDecoration: "underline",
  fontWeight: 500,
};
const totalBoxStyle = {
  backgroundColor: "#e6f7ff",
  border: "1px solid #91d5ff",
  borderRadius: "4px",
  padding: "8px 12px",
  marginTop: "8px",
  display: "flex",
  flexWrap: "wrap",
  gap: "12px",
  fontSize: "11px",
};



export default GramSabhaBDPOReport;

